import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  message
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { fetchSprTov } from "@components/health/clients/health-clients-api";
import { pick, toNumber } from "@components/health/clients/health-clients-utils";
import {
  createSprTemplateNaznItem,
  deleteSprTemplateNaznItem,
  fetchSprTemplateNaznParamNames,
  fetchSprTemplateNaznParamValues,
  saveSprTemplateNaznItem
} from "./spr-template-nazn-api";
import {
  parseParamsText,
  serializeParamsRows,
  VID_PLAN_LABELS,
  VID_PLAN_OPTIONS
} from "./spr-template-nazn-utils";

const LABEL_WIDTH = 110;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const normalizeTov = (row) => ({
  idTov: toNumber(pick(row, "idTov", "IdTov")),
  name: pick(row, "name", "Name") ?? "",
  art: pick(row, "art", "Art") ?? "",
  vidPlan: toNumber(pick(row, "vidPlan", "VidPlan")),
  isPlat: toNumber(pick(row, "isPlat", "IsPlat"))
});

/**
 * Блок строки состава шаблона — правка в той же форме (Delphi TFrmESprShbNazn).
 */
const SprTemplateNaznItemBlock = ({
  idShb,
  item,
  isNew = false,
  canEdit,
  expanded,
  onToggle,
  onSaved,
  onCancelNew,
  onDeleted
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tovOptions, setTovOptions] = useState([]);
  const [tovLoading, setTovLoading] = useState(false);
  const [paramRows, setParamRows] = useState([]);
  const [paramNameOptions, setParamNameOptions] = useState([]);
  const [paramValueOptions, setParamValueOptions] = useState({});
  const tovSearchRef = useRef(0);
  const tovTimerRef = useRef(0);
  const idUslWatch = Form.useWatch("idUsl", form);
  const isPersisted = item?.idStr != null;

  const loadTov = (q) => {
    clearTimeout(tovTimerRef.current);
    tovTimerRef.current = setTimeout(() => {
      const requestId = ++tovSearchRef.current;
      setTovLoading(true);
      fetchSprTov(q)
        .then((data) => {
          if (requestId !== tovSearchRef.current) return;
          setTovOptions(Array.isArray(data) ? data.map(normalizeTov) : []);
        })
        .catch(() => {
          if (requestId !== tovSearchRef.current) return;
          setTovOptions([]);
        })
        .finally(() => {
          if (requestId === tovSearchRef.current) setTovLoading(false);
        });
    }, 300);
  };

  useEffect(() => {
    if (!expanded) return;
    setError("");
    loadTov("");

    if (isPersisted && item) {
      form.setFieldsValue({
        idUsl: item.idUsl ?? undefined,
        cnt: toNumber(item.cnt) ?? 1,
        vidPlan: toNumber(item.vidPlan) ?? 0,
        isPlat: (toNumber(item.isPlat) ?? 0) === 1
      });
      setParamRows(parseParamsText(item.params));
      return;
    }

    form.resetFields();
    form.setFieldsValue({ cnt: 1, vidPlan: 0, isPlat: false });
    setParamRows([]);
    setParamNameOptions([]);
    setParamValueOptions({});
  }, [expanded, isPersisted, item, form]);

  useEffect(() => {
    if (!expanded || idUslWatch == null) {
      setParamNameOptions([]);
      return;
    }
    fetchSprTemplateNaznParamNames(idUslWatch)
      .then((data) => setParamNameOptions(Array.isArray(data) ? data.filter(Boolean) : []))
      .catch(() => setParamNameOptions([]));
  }, [expanded, idUslWatch]);

  const tovSelectOptions = useMemo(() => {
    const opts = tovOptions
      .filter((t) => t.idTov != null)
      .map((t) => ({
        value: t.idTov,
        label: t.art ? `${t.name} (${t.art})` : t.name,
        tov: t
      }));

    if (isPersisted && item?.idUsl != null && !opts.some((o) => o.value === item.idUsl)) {
      opts.unshift({
        value: item.idUsl,
        label: item.tovName || `#${item.idUsl}`,
        tov: null
      });
    }
    return opts;
  }, [tovOptions, isPersisted, item]);

  const onTovChange = (_idUsl, option) => {
    const tov = option?.tov;
    setParamRows([]);
    setParamValueOptions({});
    if (!tov || isPersisted) return;
    form.setFieldsValue({
      vidPlan: tov.vidPlan ?? 0,
      isPlat: (tov.isPlat ?? 0) === 1
    });
  };

  const loadParamValues = (name, rowKey) => {
    if (!idUslWatch || !name) return;
    fetchSprTemplateNaznParamValues(idUslWatch, name)
      .then((data) => {
        const list = Array.isArray(data) ? data.filter(Boolean) : [];
        setParamValueOptions((prev) => ({ ...prev, [rowKey]: list }));
      })
      .catch(() => {
        setParamValueOptions((prev) => ({ ...prev, [rowKey]: [] }));
      });
  };

  const onFinish = async (values) => {
    if (idShb == null || !canEdit) return;
    setSaving(true);
    setError("");
    const body = {
      idUsl: values.idUsl,
      cnt: values.cnt ?? 1,
      vidPlan: values.vidPlan ?? 0,
      isPlat: values.isPlat ? 1 : 0,
      params: serializeParamsRows(paramRows)
    };
    try {
      const saved = isPersisted
        ? await saveSprTemplateNaznItem(idShb, item.idStr, body)
        : await createSprTemplateNaznItem(idShb, body);
      message.success("Сохранено");
      onSaved?.(saved);
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = () => {
    if (!isPersisted || !canEdit) return;
    Modal.confirm({
      title: "Удалить?",
      content: item.tovName || `#${item.idUsl}`,
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: () =>
        deleteSprTemplateNaznItem(idShb, item.idStr)
          .then(() => {
            message.success("Строка удалена");
            onDeleted?.(item.idStr);
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  };

  const titleText = isNew
    ? "Новая процедура"
    : item?.tovName || (item?.idUsl != null ? `#${item.idUsl}` : "Процедура");

  const metaText = !isNew
    ? [
        item?.cnt != null ? `×${item.cnt}` : null,
        (toNumber(item?.isPlat) ?? 0) === 1 ? "платно" : null,
        VID_PLAN_LABELS[item?.vidPlan] || null
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const paramColumns = [
    {
      title: "Параметр",
      dataIndex: "name",
      key: "name",
      render: (_, row) => (
        <Select
          showSearch
          allowClear
          size="small"
          disabled={!canEdit}
          style={{ width: "100%" }}
          value={row.name || undefined}
          options={paramNameOptions.map((n) => ({ value: n, label: n }))}
          onChange={(name) => {
            setParamRows((prev) =>
              prev.map((r) => (r.key === row.key ? { ...r, name: name || "", value: "" } : r))
            );
            if (name) loadParamValues(name, row.key);
          }}
          onOpenChange={(visible) => {
            if (visible && row.name) loadParamValues(row.name, row.key);
          }}
          notFoundContent={null}
        />
      )
    },
    {
      title: "Значение",
      dataIndex: "value",
      key: "value",
      render: (_, row) => {
        const opts = paramValueOptions[row.key] || [];
        if (opts.length > 0) {
          return (
            <Select
              showSearch
              allowClear
              size="small"
              disabled={!canEdit}
              style={{ width: "100%" }}
              value={row.value || undefined}
              options={opts.map((v) => ({ value: v, label: v }))}
              onChange={(value) => {
                setParamRows((prev) =>
                  prev.map((r) => (r.key === row.key ? { ...r, value: value || "" } : r))
                );
              }}
              onOpenChange={(visible) => {
                if (visible && row.name) loadParamValues(row.name, row.key);
              }}
            />
          );
        }
        return (
          <Input
            size="small"
            disabled={!canEdit}
            value={row.value}
            onChange={(e) => {
              const value = e.target.value;
              setParamRows((prev) =>
                prev.map((r) => (r.key === row.key ? { ...r, value } : r))
              );
            }}
            onFocus={() => {
              if (row.name) loadParamValues(row.name, row.key);
            }}
          />
        );
      }
    },
    {
      title: "",
      key: "actions",
      width: 40,
      render: (_, row) => (
        <Button
          type="text"
          size="small"
          disabled={!canEdit}
          icon={<DeleteOutlined />}
          onClick={() => setParamRows((prev) => prev.filter((r) => r.key !== row.key))}
        />
      )
    }
  ];

  return (
    <div
      className={`spr-template-nazn-block${expanded ? " spr-template-nazn-block--open" : ""}`}
    >
      <button type="button" className="spr-template-nazn-block__head" onClick={onToggle}>
        <span className="spr-template-nazn-block__title">{titleText}</span>
        {metaText ? <span className="spr-template-nazn-block__meta">{metaText}</span> : null}
      </button>

      {expanded ? (
        <div className="spr-template-nazn-block__body">
          {error ? (
            <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
          ) : null}
          <Form
            className="spr-template-nazn-block__form"
            form={form}
            {...formItemLayout}
            colon={false}
            onFinish={onFinish}
            requiredMark={false}
            disabled={!canEdit}
          >
            <Form.Item
              label="Процедура"
              name="idUsl"
              rules={[{ required: true, message: "Укажите процедуру" }]}
            >
              <Select
                className="spr-template-nazn-block__control--usl"
                showSearch
                allowClear
                filterOption={false}
                loading={tovLoading}
                options={tovSelectOptions}
                onSearch={loadTov}
                onChange={onTovChange}
                placeholder="Поиск услуги"
              />
            </Form.Item>
            <Form.Item label="Количество" name="cnt">
              <InputNumber
                className="spr-template-nazn-block__control--cnt"
                min={0}
              />
            </Form.Item>
            <Form.Item label="Планировать" name="vidPlan">
              <Select
                className="spr-template-nazn-block__control--plan"
                options={VID_PLAN_OPTIONS}
              />
            </Form.Item>
            <Form.Item label=" " name="isPlat" valuePropName="checked">
              <Checkbox>За плату</Checkbox>
            </Form.Item>
          </Form>

          <div className="spr-template-nazn__params-title">Параметры процедуры</div>
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            columns={paramColumns}
            dataSource={paramRows}
            locale={{ emptyText: "Нет параметров" }}
          />
          {canEdit ? (
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              style={{ marginTop: 8 }}
              onClick={() =>
                setParamRows((prev) => [
                  ...prev,
                  { key: `p-${Date.now()}-${prev.length}`, name: "", value: "" }
                ])
              }
            >
              Добавить параметр
            </Button>
          ) : null}

          {canEdit ? (
            <Space className="spr-template-nazn-block__actions">
              <Button type="primary" loading={saving} onClick={() => form.submit()}>
                Сохранить
              </Button>
              {isPersisted ? (
                <Button danger onClick={onDelete}>
                  Удалить
                </Button>
              ) : null}
              {isNew ? <Button onClick={onCancelNew}>Отмена</Button> : null}
            </Space>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default SprTemplateNaznItemBlock;
